/**
 * GAP-02-COMPLETE — Derive WorkbenchFocus from BusinessWorkspaceState (SSOT) with legacy bridge fallback.
 */

import { useEffect, useMemo, useState } from 'react';
import type { WorkbenchFocus } from '@shared/business-workbench/workbench-focus';
import {
  deriveWorkbenchFocusFromWorkspaceState,
  type WorkbenchFocusAdapterOptions,
} from '@shared/business-workbench/workbench-focus-adapter';
import { useOptionalBusinessWorkbenchRuntime } from '../../components/workbench/runtime/BusinessWorkbenchRuntime';
import {
  getActiveWorkbenchCommandFocus,
  subscribeActiveWorkbenchCommandFocus,
} from '../../workbench/workbench-command-focus-bridge';

export type UseOptionalDerivedWorkbenchFocusOptions = WorkbenchFocusAdapterOptions;

export function useOptionalDerivedWorkbenchFocus(
  options: UseOptionalDerivedWorkbenchFocusOptions = {}
): WorkbenchFocus | undefined {
  const runtime = useOptionalBusinessWorkbenchRuntime();
  const [bridgedFocus, setBridgedFocus] = useState(getActiveWorkbenchCommandFocus);

  useEffect(() => {
    return subscribeActiveWorkbenchCommandFocus(() => {
      setBridgedFocus(getActiveWorkbenchCommandFocus());
    });
  }, []);

  const actionProjection = options.actionProjection;
  const variant = options.variant;
  const siteSubSurface = options.siteSubSurface;
  const root = options.root;
  const selection = options.selection;
  const fallbackRoot = options.fallbackRoot;

  return useMemo(() => {
    if (runtime) {
      return deriveWorkbenchFocusFromWorkspaceState(runtime.workspaceState, {
        ...(actionProjection !== undefined ? { actionProjection } : {}),
        ...(variant !== undefined ? { variant } : {}),
        ...(siteSubSurface !== undefined ? { siteSubSurface } : {}),
        ...(root !== undefined ? { root } : {}),
        ...(selection !== undefined ? { selection } : {}),
        ...(fallbackRoot !== undefined ? { fallbackRoot } : {}),
      });
    }
    return bridgedFocus ?? undefined;
  }, [
    runtime,
    bridgedFocus,
    actionProjection,
    variant,
    siteSubSurface,
    root,
    selection,
    fallbackRoot,
  ]);
}
